import { useAppNavigation } from '@euler/app/components/AppNavigationProvider';
import { BottomAction } from '@euler/app/components/BottomAction';
import { LinkButton } from '@euler/app/components/LinkButton';
import { StackLayout } from '@euler/app/components/StackLayout';
import { Typography } from '@euler/app/components/typography';
import { LoginAgreement } from '@euler/app/flows/auth/components/Agreement';
import { useCurrentUserDangerously } from '@euler/app/flows/auth/context';
import { useMemberAccount } from '@euler/app/flows/settings/functions/useMemberAccount';
import type { SettingsNavParams } from '@euler/app/flows/settings/screens';
import { NumberInput } from '@euler/components/NumberInput';
import { Img } from '@euler/components/image/AdvancedImage';
import { useAppLoading } from '@euler/components/loading/Provider';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  useScreenContentContainerStyle,
  wrapNavigatorScreen,
} from '@euler/functions/wrapNavigatorScreen';
import { useToggleAutomaticKeyboard } from '@euler/lib/keyboard';
import { HttpError } from '@euler/lib/request';
import { OrgUserWeixinBinding } from '@euler/model/entity';
import { PredefinedVerifyCodeScene } from '@euler/model/enum';
import { useServiceFactory } from '@euler/services/factory';
import { Maybe } from '@euler/types';
import { safeMarkDevFlag, showAlert, withPrompt } from '@euler/utils';
import { formatMoney } from '@euler/utils/formatMoney';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { nanoid } from 'nanoid/non-secure';
import { useCallback, useState } from 'react';
import {
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from 'styled-components';

const kEnableSimulation = safeMarkDevFlag(false);

type MemberAccountWithdrawRequestPreviewResult = {
  totalAmountToTransfer: number;
  isUserNameRequired: boolean;
  dailyQuota: number;
  dailyQuotaExceeded: boolean;
  recentWithdrawName?: Maybe<string>;
};

export const WithdrawScreen = wrapNavigatorScreen(
  ({ binding }: { binding: OrgUserWeixinBinding }) => {
    useToggleAutomaticKeyboard(true);
    const theme = useTheme();
    const loading = useAppLoading();
    const contentContainerStyle = useScreenContentContainerStyle();
    const { rpx } = useResponsiveContext();
    const [amount, setAmount] = useState<number>();
    const { bottom } = useSafeAreaInsets();
    const { storeService, smsService } = useServiceFactory();
    const [agreementChecked, setAgreementChecked] = useState(true);
    const appNav = useAppNavigation();
    const { user } = useCurrentUserDangerously();
    const { memberId, mobile } = user;
    const navigation = useNavigation<StackNavigationProp<SettingsNavParams>>();

    const { state: account } = useMemberAccount();
    const balance =
      account != null ? Number((account.balance / 100).toFixed(2)) : undefined;

    const disabled =
      amount == null ||
      balance == null ||
      amount < 0.3 ||
      amount > balance ||
      !agreementChecked;

    const requestWithdraw = useCallback(
      async (ticket: string, name: string | undefined) => {
        if (amount == null || mobile == null) return;

        const { ttl, requestId, length } =
          await storeService.withdrawMemberAccount(memberId, {
            ticket,
            name,
            amount: Math.floor(amount * 100),
            simulatedExistingBatchNoForDev: getTestBatchTransferNo(),
          });

        loading.hide();

        navigation.push('_verifyCode', {
          compact: false,
          codeInfo: {
            length,
            ticket,
            ttl,
          },
          mobile,
          onSendCode: async callback => {
            try {
              const res = await smsService.sendVerifyCode(
                ticket,
                mobile,
                PredefinedVerifyCodeScene.Identity,
                length,
              );
              callback(null, {
                ticket,
                ttl: res,
                length,
              });
            } catch (e) {
              callback(e as Error);
            }
          },
          onDone: async res => {
            loading.show();
            try {
              const { batchNo } =
                await storeService.confirmWithdrawMemberAccount(
                  memberId,
                  requestId,
                  { ticket, verifyCode: res.verifyCode },
                );

              navigation.goBack();
              navigation.replace('_withdrawResult', {
                batchNo,
              });
            } catch (e) {
              void showAlert((e as any).message);
              navigation.goBack();
            } finally {
              loading.hide();
            }
          },
        });
      },
      [amount, loading, memberId, mobile, navigation, smsService, storeService],
    );

    const promptForName = useCallback(
      async (
        recentWithdrawName: string | undefined,
      ): Promise<string | undefined> => {
        if (Platform.OS === 'web') {
          const name = await withPrompt({
            title: '确认您的真实姓名',
            defaultValue: recentWithdrawName,
          });
          return name;
        }
        return await new Promise(resolve => {
          appNav.navigate('_prompt', {
            title: '确认您的真实姓名',
            cancelButtonText: '取消',
            confirmButtonText: '确认提现',
            confirmButtonDisabled: !recentWithdrawName,
            defaultValue: recentWithdrawName,
            inputStyle: {
              textAlign: 'center',
              fontFamily: FontFamily.NotoSans.Medium,
            },
            onChange: (value, notify$) => {
              const isValid = /^[\u4e00-\u9fa5]{2,4}$/.exec(value.trim());
              notify$.next({
                type: 'button',
                button: 'confirm',
                disabled: !isValid,
              });
            },
            onConfirm: async value => {
              resolve(value);
            },
            onCancel: () => {
              resolve(undefined);
            },
          });
        });
      },
      [appNav],
    );

    const initiateWithdrawRequest = useCallback(
      async (ticket: string) => {
        try {
          await requestWithdraw(ticket, undefined);
        } catch (e) {
          loading.hide();
          if (e instanceof HttpError) {
            if (e.subCode === 'withdraw_daily_quota_exceeded') {
              const extra = e.extra as
                | MemberAccountWithdrawRequestPreviewResult
                | undefined;
              void showAlert(
                `对不起，您今日累计提现额已超出您单日提现限额${formatMoney(
                  extra?.dailyQuota,
                )}元!`,
              );
              return;
            }
            if (e.subCode === 'withdraw_username_required') {
              const extra = e.extra as
                | MemberAccountWithdrawRequestPreviewResult
                | undefined;
              const name = await promptForName(
                extra?.recentWithdrawName ?? undefined,
              );
              if (name) {
                try {
                  loading.show();
                  await requestWithdraw(ticket, name);
                } catch (err) {
                  loading.hide();
                  void showAlert((err as any).message);
                }
              }
              return;
            }
          }
          void showAlert((e as any).message);
        }
      },
      [loading, promptForName, requestWithdraw],
    );

    const onConfirm = useCallback(async () => {
      if (!mobile) {
        void showAlert('Mobile number is required');
        return;
      }

      setTimeout(async () => {
        try {
          loading.show();
          const nonce = nanoid(40);
          const ticket = await smsService.requestVerifyCodeTicket(nonce);
          await initiateWithdrawRequest(ticket);
        } catch (e) {
          loading.hide();
          void showAlert((e as any).message);
        }
      }, 350);
    }, [initiateWithdrawRequest, loading, mobile, smsService]);

    return (
      <KeyboardAvoidingView
        behavior={'padding'}
        style={StyleSheet.absoluteFill}
        keyboardVerticalOffset={rpx(44) + bottom}
        css={`
          flex: 1;
        `}
      >
        <ScrollView
          keyboardShouldPersistTaps="handled"
          contentContainerStyle={[
            {
              paddingTop: rpx(44),
              paddingRight: rpx(30),
              paddingLeft: rpx(30),
              paddingBottom: rpx(44) + bottom,
              flexGrow: 1,
            },
            contentContainerStyle,
          ]}
        >
          <StackLayout direction="column" alignItems="center" gap={rpx(8)}>
            <Img
              uri={binding.headImgUrl}
              css={`
                width: ${rpx(90)}px;
                height: ${rpx(90)}px;
                border-radius: ${rpx(8)}px;
              `}
            />
            {binding.nick ? (
              <Typography.Label
                size={rpx(14)}
                align="center"
                lineHeight={rpx(18)}
              >
                {binding.nick}
              </Typography.Label>
            ) : null}
            <StackLayout
              direction="column"
              alignItems="center"
              gap={rpx(8)}
              css={`
                padding-top: ${rpx(15)}px;
              `}
            >
              <Typography.Label
                bold
                size={rpx(50)}
                align="center"
                lineHeight={rpx(64)}
              >
                {formatMoney(account?.balance, { prefix: true })}
              </Typography.Label>
              <Typography.Label
                light
                size={rpx(14)}
                align="center"
                color="#777"
              >
                当前可提现余额
              </Typography.Label>
            </StackLayout>
          </StackLayout>
          <StackLayout
            direction="column"
            gap={rpx(8)}
            css={`
              margin-top: ${rpx(80)}px;
              flex: 1;
            `}
          >
            <NumberInput
              value={amount}
              onChangeValue={setAmount}
              placeholder="请输入提现金额"
              placeholderTextColor={'#aaa'}
              returnKeyLabel="确认"
              returnKeyType="done"
              keyboardType="numbers-and-punctuation"
              inputMode="decimal"
              autoCapitalize="none"
              autoCorrect={false}
              css={`
                text-align: center;
                padding: ${rpx(15)}px;
                background-color: #e5e5e5;
                border-radius: ${rpx(30)}px;
                font-weight: 700;
                font-size: ${rpx(16)}px;
              `}
            />
            <StackLayout
              direction="row"
              alignItems="center"
              justifyContent="center"
            >
              <Typography.Label
                light
                color="#777"
                align="center"
                size={rpx(12)}
              >
                {`最低提现0.3元, `}
              </Typography.Label>
              <LinkButton
                text="全部提现"
                disabled={balance == null}
                textStyle={{ fontSize: rpx(12) }}
                onPress={() => setAmount(balance)}
              />
            </StackLayout>
          </StackLayout>
          <StackLayout
            alignItems="stretch"
            gap={rpx(8)}
            css={`
              margin-top: ${rpx(44)}px;
              align-items: center;
            `}
          >
            <BottomAction.Button
              text="确认提现"
              variant={disabled ? 'info' : 'primary'}
              disabled={disabled}
              disabledStyle={{ backgroundColor: '#777' }}
              onPress={onConfirm}
              css={`
                align-self: stretch;
              `}
            />
            <LoginAgreement
              checked={agreementChecked}
              label="确认提现即表示同意我们的"
              checkboxColor={theme.link}
              checkboxSize={rpx(15)}
              labelStyle={{ fontSize: rpx(12) }}
              linkStyle={{ color: theme.link }}
              onChange={setAgreementChecked}
            />
          </StackLayout>
        </ScrollView>
      </KeyboardAvoidingView>
    );
  },
  {
    title: '奖励提现',
  },
);

function getTestBatchTransferNo(): string | undefined {
  return kEnableSimulation ? '88SeaYngNsD7vwwIUuUWRgaWeyvtCQhK' : undefined;
}
